import { useState } from "react";
import { useConfirmation } from "context/Mutation/ConfirmationDialogContext";
import { Box, Button, HStack, Text, useToast } from "@chakra-ui/react";
import { CheckCircle, Copy, Info, Sparkle, Trash, WarningOctagon } from "@phosphor-icons/react";
import { emitAIEvent, removeExtraInstructions } from "api/ai/ai.service";
import fileinfoService from "api/common/fileinfo.service";
import { SmallText } from "components/text/TextTypes";
import DevOnly from "context/Auth/DevOnly";
import { multipartAnswerToString } from "pages/ISSB/IssbDataInput/helper";
import { useQuery } from "react-query";
import { colors } from "theme/foundations/colors";
import { showSuccessToast } from "utils";
import { copyToClipboard, markdownToHtml, stripHtml } from "utils/text-utils";
import { RichTextViewer } from "../../IssbDataInput/components/RichTextEditor/RichTextEditor";
import { FileList } from "../FileListWithBrowseOrUpload";
import { AIFeedbackComment, AIFeedbackThumbsUpDown } from "./AIFeedbackControls";
import LifecycleState from "api/common/LifecycleState";
import { ACTION_applyAnswer, apiGetThread, apiPostThreadUpdate } from "api/ai/ai.service";
import { truncate } from "lodash";
import { extractAndCountJsonObjects } from "utils/chat-utils";
/**
 *
 *
 * @param {Object} props
 * @param {!ChatMessage} props.message
 * @param {boolean} props.isLastMessage
 * @param {?FileInfo[]} props.fileinfos
 * @returns
 */
export default function AIChatMessage({ threadId, message, setMessages, allMessages, isLastMessage, debug }) {
  const { toast } = useToast();

  let isAI = "assistant tool critic system".includes(message.role);
  let messageType = "ChatMessage"; // default placeholder in case of unknown
  let msg = message?.content;

  if (message?.json_content) {
    // Prefer structured content if available
    messageType = message.json_content?.type;

    // Handle message content with proper precedence
    if (message.json_content.message) {
      msg = message.json_content.message; // Note: can be "" / unset
    } else if (message.json_content.answer) {
      msg =
        typeof message.json_content.answer === "string"
          ? message.json_content.answer
          : multipartAnswerToString(message.json_content.answer);
    } else if (message.json_content.type === "function" && message.json_content.function) {
	  const fn = message.json_content.function;
      msg = "Tool call: " +fn.name + " - " + truncate(JSON.stringify(fn.arguments), {length: 512});
    }
  } else if (isAI) {
    // Fallback to parsing raw content
	if ( ! message.error) {
		try {
			const parsedContent = JSON.parse(message.content);
			messageType = parsedContent.type;
			msg = parsedContent.message || parsedContent.answer;
		} catch (e) {
			// Raw content is not JSON, use as-is (can happen with AI chat forgetting to use the right format)
			console.log("(swallow) AI message content is not valid JSON.", message?.content);
		}	
	}
  }
  if (message.role === "tool") {
	msg = "Tool results: " + truncate(message.content, {length: 512});
  }
  
  // If there is no message at all, show something
  if (!msg || msg === "null") {
    console.warn("No message content!", message);
    msg = `Type: ${messageType}. <br/> No chat response available.`;
  }

  // HACK: remove any extra instructions from the message
  msg = debug ? msg : removeExtraInstructions(msg);

  // HACK detect error message (odd output seen Oct 2, 2025)
  if (message.error || message.rate_limit_error || msg?.includes("ThrottlingException")) {
	if ( ! isAI) {
		console.error("Error message but not AI?!", {...message});
	}
	isAI = true;
  }

  // HACK: the LLM can include a data-request or tool-call in the message.
  // e.g. "To properly address this question, we would need more targeted information.
  // { "type": "data_request", "request": "Please provide any available data on Morgan Stanley's refrigerant consumption" }
  if (msg?.includes("data_request") || msg?.includes('"type": "function"')) {
    // remove json from message
    msg = msg.replace(/```json[\s\S]*?```/, "");
    msg = msg.replace(/{[\s\S]*?}/, "").trim();
    if (message.json_content?.type === "data_request") {
      msg = "\n\nData request: " + message.json_content.request;
    }
    if (!msg) {
      msg = "To properly address this question we need more information.";
    }
  }

  const doCopyToClipboard = async () => {
    copyToClipboard(msg);
  };


  const handleApplyAnswer = () => {
    // Convert any markdown in the answer to HTML.
    let messageToPost = message;
    if (messageToPost.json_content?.message) {
      messageToPost.json_content.message = markdownToHtml(messageToPost.json_content.message);
    } else if (messageToPost.json_content?.answer) {
      messageToPost.json_content.answer =
        typeof messageToPost.json_content.answer === "string"
          ? markdownToHtml(messageToPost.json_content.answer)
          : messageToPost.json_content.answer;
    }
    // wiring - rely on AI context
    emitAIEvent({ action: ACTION_applyAnswer, message: messageToPost });
  };

  // Note: state var defined here for sharing between two widgets -- actual use is in AIFeedbackControls.jsx
  const [feedback, setFeedback] = useState();
  const [showFeedbackComment, setShowFeedbackComment] = useState(); // start unset - the comment widget will initialise

  // FIXME When the source is round questions, we don't get files just json_content.sources
  // HACK files used
  const fileIds = message.json_content?.files;
  const { data: fileinfosPage, isLoading: isLoadingFileInfos } = useQuery(
    ["fileinfoService.list", "" + fileIds],
    () => fileinfoService.list({ ids: fileIds }),
    { enabled: !!fileIds?.length }
  );
  const fileinfos = fileinfosPage?.content;
  const sourceSlices = message.json_content?.sourceSlices;
  fileinfos?.forEach((fileinfo) => {
    const slices = sourceSlices?.filter((slice) => "" + slice.fileinfoId === "" + fileinfo.id); // Note: paranoia on long/string ids
    if (slices?.length) {
      const pages = slices.map((slice) => slice.page);
      fileinfo.pages = pages.join(", "); // HACK to show page numbers
    }
  });

  // e.g. type = answer, dashboard_widget
  const type = message.json_content?.type;

  const { confirm } = useConfirmation();
  const archiveMessage = async (message, threadId) => {
    console.warn("archiveMessage", message, threadId);
    const confirmed = await confirm({
      title: "Delete message",
      message: "Are you sure you want to delete (archive) this message?",
      confirmText: "Delete",
      cancelText: "Cancel",
    });
    if (!confirmed) return;
    // load the thread (ensure we're up to date)
    const thread = await apiGetThread(threadId);
    // get the fresh message and mark as archived on both copies (for the UI and the save)
    message.state = LifecycleState.ARCHIVED;
      //Use the thread array and message index to delete it.
      const freshUserMessageIndex = allMessages.indexOf(message);
      const freshUserMessage = thread.messages[freshUserMessageIndex];
      freshUserMessage.state = LifecycleState.ARCHIVED;
      // console.warn("user message state = archived", message);
    // save the thread
    await apiPostThreadUpdate(thread)
      .then((data) => {
        setMessages(data.messages);
      })
      .catch((err) => {
        // console.log("status", err.response?.status);
        // console.log("allow", err.response?.headers?.allow);
        // console.log("data", err.response?.data);
        throw err;
      });
    showSuccessToast(toast, "Message archived");
  }; // ./archiveMessage

  const checks = message.checks || {};
  const failedChecks = Object.entries(checks).filter(([_, check]) => check?.state === "fail");
  const checksMessage = "Checks run on this response:\n"
  + Object.entries(checks).filter(([_, check]) => check).map(([key, check]) => `${key}: ${check?.message || check?.state || JSON.stringify(check)}`).join("\n");

  return (
    <HStack position="relative">
      {!isAI && <Box flexGrow={1} />}
      <Box
        bg={isAI ? "white" : colors.darkTeal}
        alignSelf={message.role === "user" ? "flex-end" : "flex-start"}
        mt={3}
        p={2}
        maxW="90%"
        borderRadius="md"
        dropShadow="md"
		opacity={message.state === LifecycleState.ERROR || message.state === LifecycleState.ARCHIVED ? 0.6 : 1}
      >
        {/*
		  - RichTextViewer is Global use across multiple places for consistency.
		  - It supports a limited subset of HTML tags and styles.
		  - markdownToHtml is used to convert if AI produce markdown to HTML. So the output will always be HTML.
		  */}
        <Box display="flex" flexDirection="row">
          <RichTextViewer minH={0} p={0} color={isAI ? "darkTeal" : "white"} content={markdownToHtml(msg)} />
          {!isAI /* delete for user messages */ && (
            <DevOnly style={{ marginLeft: 10 }}>
              <Button variant="ghost" fontSize="body.xxs" size="xxs" onClick={() => archiveMessage(message, threadId)}>
                <Trash size={18} color={colors.esg_white} />
              </Button>
            </DevOnly>
          )}
        </Box>
        {fileinfos?.length > 0 && (
          <Box>
            <SmallText>Sources</SmallText>
            <FileList fileInfoList={fileinfos} />
          </Box>
        )}
        {isAI && (
          <>
		  {debug && message.json_content && 
		  <><Text>Type:{type}</Text>
		  {message.json_content.sources && <Text>Source Slices in Context: {message.json_content.sources.length}</Text>}
		  {message.json_content.sourceSlices && <Text>Source Slices Used: {message.json_content.sourceSlices.map(
			(slice) => slice.title || slice.question_number).join(", ")}</Text>}		
		  <Text>Other fields: {Object.keys(message.json_content).filter((key) => (""+message.json_content[key]) !== "" && ! ["type", "sources", "sourceSlices", "function"].includes(key)).join(", ")}</Text>
		  </>
		  }
      {
        debug && (extractAndCountJsonObjects(message.content) > 1) &&
        <Text>Sorry: There was a formatting error in the AI output</Text>
      }
		  {debug && (message.issue || message.error) && 
		   <Text>Issue: {message.issue || message.error}</Text>
		   }
            <HStack justifyContent="space-between" mt={1}>
              <Sparkle size={24} color={colors.navIconLime} />
              <HStack>
                {type === "answer" && (
                  <Button
                    fontSize="body.xs"
                    onClick={handleApplyAnswer}
                    variant={isLastMessage ? "primary" : "secondary"}
                    size="sm"
                    /* Note: if the user doesnt have canSave it'd be nice to alter "Apply" to "Save Draft" but is that worth the faff for now? */
                  >
                    Apply answer
                  </Button>
                )}
                <AIFeedbackThumbsUpDown
                  threadId={threadId}
                  message={message}
                  feedback={feedback}
                  setFeedback={setFeedback}
                  setShowFeedbackComment={setShowFeedbackComment}
                />
                <Button variant="ghost" size="sm" onClick={doCopyToClipboard}>
                  <Copy size={24} color={colors.navIconLime} />
                </Button>

				{message.checks && Object.keys(message.checks).length > 0 && 
				<DevOnly>
				 <Button /* debug button */				  
                    variant="ghost"
                    fontSize="body.xxs"
                    size="xs"
					title="Show check info"
                    onClick={() => {console.log("clicked-message: checks", message.checks); alert(checksMessage)}}
                  >
						<CheckCircle size={24} color={failedChecks?.length > 0? colors.esg_red : colors.navIconLime} />
                  </Button>
                </DevOnly>}

                <DevOnly>
                  <Button /* debug button */				  
                    variant="ghost"
                    fontSize="body.xxs"
                    size="xs"
					title="Show debug message + log to console"
                    onClick={() => {console.log("clicked-message", message); alert(JSON.stringify(message)); }}
                  >
                    <Info size={24} color={colors.navIconLime} />
                  </Button>
                </DevOnly>
                {message?.json_content?.error && (
                  <Button
                    variant="ghost"
                    fontSize="body.xxs"
                    size="xs"
                    onClick={() => alert(message.json_content.error)}
                  >
                    <WarningOctagon size={24} color={colors.esg_red} />
                  </Button>
                )}
                <DevOnly>
                  <Button
                    variant="ghost"
                    fontSize="body.xxs"
                    size="xs"
                    onClick={() => archiveMessage(message, threadId)}
                  >
                    <Trash size={24} color={colors.navIconLime} />
                  </Button>
                  {message?.state && message.state !== "ACTIVE" && <Text>{message.state}</Text>}
                </DevOnly>
              </HStack>
            </HStack>
            <AIFeedbackComment
              threadId={threadId}
              message={message}
              feedback={feedback}
              setFeedback={setFeedback}
              showFeedbackComment={showFeedbackComment}
              setShowFeedbackComment={setShowFeedbackComment}
            />
          </>
        ) /* ./isAi */}
		{!isAI && debug && <Text>State: {message.state}</Text>}
      </Box>
      {isAI && <Box flexGrow={1} /> /* doc: why?? */}
    </HStack>
  );
} // ./Message
